/** Carga del libro de guías desde Excel, con informe de lo corregido. */

import { useRef, useState } from 'react';
import { FileSpreadsheet, Upload, TriangleAlert, CheckCircle2 } from 'lucide-react';
import { Boton, Chip, Tarjeta, TarjetaTitulo } from '@/components/ui';
import { importarLibroGuias, type InformeImportacion } from '@/db/importarExcel';
import { useCatalogos } from '@/hooks/useCatalogos';
import { m3 as fm3, num } from '@/domain/formato';

export default function ImportarExcel() {
  const { recargar } = useCatalogos();
  const entrada = useRef<HTMLInputElement>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [progreso, setProgreso] = useState({ pct: 0, etapa: '' });
  const [informe, setInforme] = useState<InformeImportacion | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function alElegir(archivo: File | undefined) {
    if (!archivo) return;
    setError(null);
    setInforme(null);
    setTrabajando(true);
    setProgreso({ pct: 0, etapa: 'Abriendo el archivo…' });
    try {
      const inf = await importarLibroGuias(archivo, (pct, etapa) => setProgreso({ pct, etapa }));
      setInforme(inf);
      await recargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer el archivo.');
    } finally {
      setTrabajando(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  return (
    <Tarjeta>
      <TarjetaTitulo>Cargar libro de guías desde Excel</TarjetaTitulo>

      <div className="space-y-3 p-4">
        <p className="text-xs leading-relaxed text-texto-mute">
          Acepta el libro tal como está hoy — una hoja por planta («NV 17», «KM 13», «VALLE») con
          las columnas de siempre. Al leerlo corrige las columnas invertidas, reconstruye los
          horarios que cruzan la medianoche, marca como anuladas las guías «NULA» conservando su
          folio, y resuelve clientes, productos y equipos contra los catálogos. Las guías que ya
          estén cargadas no se duplican.
        </p>

        <input
          ref={entrada}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={(e) => void alElegir(e.target.files?.[0])}
        />

        <Boton
          variante="primario"
          icono={<Upload size={15} />}
          onClick={() => entrada.current?.click()}
          disabled={trabajando}
        >
          {trabajando ? 'Importando…' : 'Elegir archivo .xlsx'}
        </Boton>

        {trabajando && (
          <div>
            <div className="h-1.5 overflow-hidden rounded-full bg-petroleo-800">
              <div
                className="h-full rounded-full bg-fluor-verde transition-all"
                style={{ width: `${progreso.pct}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-texto-mute">{progreso.etapa}</p>
          </div>
        )}

        {error && (
          <div className="rounded-control border border-fluor-rojo/40 bg-fluor-rojo/10 px-3 py-2 text-xs text-fluor-rojo">
            {error}
          </div>
        )}

        {informe && <Informe inf={informe} />}
      </div>
    </Tarjeta>
  );
}

function Informe({ inf }: { inf: InformeImportacion }) {
  const correcciones = [
    inf.columnasCorregidas > 0 && `${num(inf.columnasCorregidas)} filas con TURNO y N° REMITO invertidos`,
    inf.crucesMedianoche > 0 && `${num(inf.crucesMedianoche)} horarios que cruzaban la medianoche`,
    inf.anuladas > 0 && `${num(inf.anuladas)} guías «NULA» registradas como anuladas`,
  ].filter(Boolean) as string[];

  const pendientes = [
    inf.sinFecha > 0 && `${num(inf.sinFecha)} filas sin fecha`,
    inf.duplicadas.length > 0 && `${num(inf.duplicadas.length)} folios ya cargados`,
    inf.tiemposIncompletos > 0 && `${num(inf.tiemposIncompletos)} guías sin los 6 horarios`,
    inf.sinProducto.length > 0 && `${inf.sinProducto.length} productos no reconocidos`,
    inf.sinCliente.length > 0 && `${inf.sinCliente.length} clientes no reconocidos`,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-3 rounded-tarjeta border border-linea bg-petroleo-800/40 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-fluor-verde">
        <CheckCircle2 size={16} />
        {num(inf.importadas)} guías importadas · {fm3(inf.m3Total)} m³
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          ['Importadas', num(inf.importadas)],
          ['Omitidas', num(inf.omitidas)],
          ['Anuladas', num(inf.anuladas)],
          ['Bloques sobrestadía', num(inf.bloquesSobrestadia)],
        ].map(([t, v]) => (
          <div key={t} className="rounded-control bg-petroleo-900/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-texto-mute">{t}</div>
            <div className="font-mono text-base tabular-nums">{v}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-texto-mute">
          Por hoja
        </div>
        <div className="flex flex-wrap gap-1.5">
          {inf.hojas.map((h) => (
            <Chip key={h.nombre} nivel={h.importadas > 0 ? 'VERDE' : 'NEUTRO'}>
              <FileSpreadsheet size={11} /> {h.nombre}: {num(h.importadas)}
            </Chip>
          ))}
        </div>
      </div>

      {correcciones.length > 0 && (
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-texto-mute">
            Corregido automáticamente
          </div>
          <ul className="space-y-0.5 text-xs text-texto-mute">
            {correcciones.map((c) => (
              <li key={c} className="flex items-start gap-1.5">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-fluor-verde" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {pendientes.length > 0 && (
        <div>
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-fluor-amarillo">
            <TriangleAlert size={12} /> Requiere revisión
          </div>
          <ul className="space-y-0.5 text-xs text-texto-mute">
            {pendientes.map((p) => (
              <li key={p} className="flex items-start gap-1.5">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-fluor-amarillo" />
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}

      {inf.sinProducto.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-texto-mute hover:text-texto">
            Ver los {inf.sinProducto.length} productos no reconocidos
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {inf.sinProducto.map((p) => (
              <Chip key={p} nivel="AMARILLO">
                {p}
              </Chip>
            ))}
          </div>
          <p className="mt-2 text-texto-mute">
            Estas guías entraron marcadas «por clasificar»: no se les aplicaron recargos
            automáticos. Agrégalos al catálogo de productos y vuelve a valorizarlas.
          </p>
        </details>
      )}

      {inf.sinCliente.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-texto-mute hover:text-texto">
            Ver los {inf.sinCliente.length} clientes no reconocidos
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {inf.sinCliente.map((c) => (
              <Chip key={c} nivel="AMARILLO">
                {c}
              </Chip>
            ))}
          </div>
          <p className="mt-2 text-texto-mute">
            El nombre se guardó tal cual vino. Para que sumen junto al cliente correcto, agrégalos
            como alias en el catálogo.
          </p>
        </details>
      )}
    </div>
  );
}
